import { Card, Badge, InfoRow } from '../shared';

function SectionHeader({ title, tabId, onEdit }) {
    return (
        <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
            <button
                type="button"
                onClick={() => onEdit(tabId)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
                Edit
            </button>
        </div>
    );
}

function EnabledBadge({ enabled }) {
    return (
        <Badge variant={enabled !== false ? 'success' : 'outline'}>
            {enabled !== false ? 'Enabled' : 'Disabled'}
        </Badge>
    );
}

function ListValue({ items, fallback = 'All' }) {
    if (!items || items.length === 0) return <span className="text-gray-400">{fallback}</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {items.map(item => <Badge key={item} variant="outline">{item}</Badge>)}
        </div>
    );
}

const capabilityOn = (dict) => dict == null || dict.enabled !== false;

function CapabilityRow({ label, dict, children }) {
    const enabled = capabilityOn(dict);
    return (
        <div>
            <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-gray-700">{label}</span>
                <EnabledBadge enabled={enabled} />
            </div>
            {enabled && children && <div className="ml-4 space-y-0.5">{children}</div>}
        </div>
    );
}

export default function TabReview({ formData, isCreate, knowledgeItems, onEditSection }) {
    return (
        <div className="space-y-4">
            <Card title="Identity">
                <SectionHeader title="Identity" tabId="general" onEdit={onEditSection} />
                <div className="space-y-0.5">
                    {isCreate && formData.id && <InfoRow label="ID" value={formData.id} mono />}
                    <InfoRow label="Name" value={formData.name} />
                    <InfoRow label="Type" value={<Badge variant="info">{formData.agent_type}</Badge>} />
                    <InfoRow label="Description" value={formData.description || null} wrap />
                    <InfoRow label="Owner" value={
                        <span>{formData.owner_type}{formData.owner_id ? `: ${formData.owner_id}` : ''}</span>
                    } />
                    <InfoRow label="Public" value={formData.is_public ? 'Yes' : 'No'} />
                </div>
            </Card>

            <Card title="Model & Reasoning">
                <SectionHeader title="Model & Reasoning" tabId="general" onEdit={onEditSection} />
                <div className="space-y-0.5">
                    <InfoRow label="Model" value={formData.model_name} mono />
                    <InfoRow label="Temperature" value={formData.temperature} />
                    <InfoRow label="Max Tokens" value={formData.max_tokens || <span className="text-gray-400">Default</span>} />
                    <InfoRow label="Reasoning" value={
                        formData.reasoning_enabled
                            ? <span>Enabled ({formData.reasoning_effort || 'medium'})</span>
                            : <span className="text-gray-400">Disabled</span>
                    } />
                </div>
            </Card>

            <Card title="Voice">
                <SectionHeader title="Voice" tabId="general" onEdit={onEditSection} />
                <div className="space-y-0.5">
                    <InfoRow label="Status" value={<EnabledBadge enabled={formData.voice_enabled} />} />
                    {formData.voice_enabled && (
                        <>
                            <InfoRow label="Model" value={formData.voice_model || <span className="text-gray-400">Default</span>} />
                            <InfoRow label="Speed" value={formData.voice_speed || 1.0} />
                        </>
                    )}
                </div>
            </Card>

            {formData.system_prompt && (
                <Card title="System Prompt">
                    <SectionHeader title="System Prompt" tabId="general" onEdit={onEditSection} />
                    <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 max-h-40 overflow-y-auto">
                        <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap">{formData.system_prompt.slice(0, 500)}{formData.system_prompt.length > 500 ? '...' : ''}</pre>
                    </div>
                    <div className="flex justify-end mt-1 text-xs text-gray-400">
                        <span>{formData.system_prompt.length.toLocaleString()} chars</span>
                        <span className="mx-2">·</span>
                        <span>~{Math.ceil(formData.system_prompt.length / 4).toLocaleString()} tokens</span>
                    </div>
                </Card>
            )}

            <Card title="Capabilities">
                <SectionHeader title="Skills, Subagents & Reminders" tabId="capabilities" onEdit={onEditSection} />
                <div className="space-y-3">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-700">Skills</span>
                            <EnabledBadge enabled={formData.skills?.enabled !== false} />
                        </div>
                        {(formData.skills?.enabled !== false) && (
                            <div className="ml-4 space-y-0.5">
                                <InfoRow label="Include" value={<ListValue items={formData.skills?.include} fallback="All" />} />
                                <InfoRow label="Include tags" value={<ListValue items={formData.skills?.include_tags} fallback="—" />} />
                                <InfoRow label="Exclude" value={<ListValue items={formData.skills?.exclude} fallback="None" />} />
                                <InfoRow label="Exclude tags" value={<ListValue items={formData.skills?.exclude_tags} fallback="—" />} />
                                <InfoRow
                                    label="Preload"
                                    value={
                                        formData.skills?.preload === null || formData.skills?.preload === undefined
                                            ? <span className="text-gray-500 italic">All accessible</span>
                                            : formData.skills.preload.length === 0
                                                ? <span className="text-amber-600 italic">None — discovery-only mode (ls /skills/)</span>
                                                : <ListValue items={formData.skills.preload} />
                                    }
                                />
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-700">Subagents</span>
                            <EnabledBadge enabled={formData.subagents?.enabled !== false} />
                        </div>
                        {formData.subagents?.enabled !== false && (
                            <div className="ml-4 space-y-1">
                                <InfoRow label="Sources" value={<ListValue items={formData.subagents?.sources || []} fallback="All" />} />
                                <InfoRow label="Stream subgraphs" value={formData.subagents?.stream_subgraphs !== false ? 'Enabled' : 'Disabled'} />
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-700">Reminders</span>
                            <EnabledBadge enabled={formData.reminders_enabled} />
                        </div>
                        {formData.reminders_enabled !== false && (
                            <div className="ml-4 space-y-0.5">
                                <InfoRow label="Include" value={<ListValue items={formData.reminders_include} />} />
                                <InfoRow label="Exclude" value={<ListValue items={formData.reminders_exclude} fallback="None" />} />
                                <InfoRow label="Max/turn" value={formData.reminders_max_per_turn ?? 5} />
                            </div>
                        )}
                    </div>
                    {!isCreate && knowledgeItems.length > 0 && (
                        <div>
                            <span className="text-sm font-medium text-gray-700">Knowledge</span>
                            <div className="ml-4 mt-1 flex flex-wrap gap-1">
                                {knowledgeItems.map(k => (
                                    <Badge key={k.id} variant="outline">{k.topic}</Badge>
                                ))}
                            </div>
                        </div>
                    )}
                    <CapabilityRow label="Context Editing" dict={formData.context_editing}>
                        <InfoRow label="Trigger" value={formData.context_editing?.trigger ?? <span className="text-gray-400 italic">Default</span>} />
                        <InfoRow label="Keep" value={formData.context_editing?.keep ?? <span className="text-gray-400 italic">Default</span>} />
                        <InfoRow label="Exclude tools" value={<ListValue items={formData.context_editing?.exclude_tools} fallback="Defaults" />} />
                        <InfoRow label="Offload tools" value={<ListValue items={formData.context_editing?.offload_tools} fallback="Defaults" />} />
                    </CapabilityRow>
                    <CapabilityRow label="Long-term Memory" dict={formData.long_term_memory} />
                    <CapabilityRow label="Image Search" dict={formData.image_search} />
                    <CapabilityRow label="Visualizer" dict={formData.visualizer} />
                    <CapabilityRow label="Sandbox" dict={formData.sandbox}>
                        <InfoRow
                            label="Simplize SDK"
                            value={(formData.sandbox?.simplize_sdk ?? true) !== false ? 'Enabled' : 'Disabled'}
                        />
                    </CapabilityRow>
                </div>
            </Card>

            <Card title="Tools & Safety">
                <SectionHeader title="Tools, MCP & HITL" tabId="tools_safety" onEdit={onEditSection} />
                <div className="space-y-3">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-700">Tools</span>
                            <EnabledBadge enabled={formData.tools?.enabled !== false} />
                        </div>
                        {formData.tools?.enabled !== false && (
                            <div className="ml-4 space-y-0.5">
                                <InfoRow label="Include Tags" value={<ListValue items={formData.tools?.include_tags} />} />
                                <InfoRow label="Exclude Tags" value={<ListValue items={formData.tools?.exclude_tags} fallback="None" />} />
                                <InfoRow label="Include" value={<ListValue items={formData.tools?.include} />} />
                                <InfoRow label="Exclude" value={<ListValue items={formData.tools?.exclude} fallback="None" />} />
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-700">MCP Servers</span>
                            <EnabledBadge enabled={formData.mcp_enabled} />
                        </div>
                        {formData.mcp_enabled !== false && (
                            <div className="ml-4 space-y-0.5">
                                <InfoRow label="Include" value={<ListValue items={formData.mcp_include} />} />
                                <InfoRow label="Exclude" value={<ListValue items={formData.mcp_exclude} fallback="None" />} />
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-700">HITL</span>
                            <EnabledBadge enabled={formData.hitl_enabled} />
                        </div>
                        {formData.hitl_enabled !== false && (
                            <div className="ml-4">
                                <InfoRow label="Interrupt" value={formData.interrupt_on ? 'Custom rules' : 'Default rules'} />
                            </div>
                        )}
                    </div>
                </div>
            </Card>

        </div>
    );
}
